/*******************
@Purpose : client-side mirror of VehicleFilterTrait::filters()' category/
           brand/color/seats/price matching (falcon-api), run against a pool
           of vehicles already fetched for the current location+dates. Dates
           themselves aren't re-checked here - every vehicle in the pool is
           already availability-filtered for those dates by the fetch that
           built the pool (see useAvailabilityPool), so every filter
           dropdown can compute its own counts/zero-result options instantly
           off one shared array instead of firing its own network request.
@Author : INIC
******************/

/*******************
@purpose : color ids come back encrypted (App\Traits\ApiResourceTrait
           encrypts every `id` field) with a random IV per request, so the
           same color gets a different ciphertext in the colorList fetch
           than it does embedded in each pool vehicle's color_detail - a
           plain id === id check across those two responses never matches.
           `name` isn't encrypted and is stable, so isolation checks/counts
           translate the real (encrypted) selected ids to names via the
           page's own colorList before comparing against
           vehicle.color_detail.name - safe because a colorId only ever
           needs to resolve within the same colorList array that produced
           it (the user picked it from there), never across requests.
@Parameter : {colorIds, colorList}
@Author : INIC
******************/
export const colorIdsToNames = (colorIds, colorList) => {
  if (!colorIds?.length || !colorList?.length) return [];
  const idToName = new Map(colorList.map((color) => [String(color.id), color.name]));
  return colorIds.map((id) => idToName.get(String(id))).filter(Boolean);
};

/*******************
@purpose : true when a vehicle satisfies every filter passed - category and
           brand match by slug (category also matches via the vehicle's
           similar categories, same as the backend's orWhereHas), color
           matches by name (see colorIdsToNames), seats match by exact
           value, price is an inclusive [min, max]
@Parameter : {vehicle, filters: {categorySlugs, brandSlugs, colorIds, seats, minPrice, maxPrice}, colorList}
@Author : INIC
******************/
export const vehicleMatchesFilters = (vehicle, filters = {}, colorList = []) => {
  const {
    categorySlugs = [],
    brandSlugs = [],
    colorIds = [],
    seats = [],
    minPrice,
    maxPrice,
  } = filters;

  if (categorySlugs.length) {
    const vehicleCategorySlugs = [
      vehicle?.category_detail?.slug,
      ...(vehicle?.similar_category_detail || []).map((category) => category?.slug),
    ].filter(Boolean);
    if (!vehicleCategorySlugs.some((slug) => categorySlugs.includes(slug))) return false;
  }

  if (brandSlugs.length && !brandSlugs.includes(vehicle?.brand_detail?.slug)) return false;

  if (colorIds.length) {
    const colorNames = colorIdsToNames(colorIds, colorList);
    if (!colorNames.includes(vehicle?.color_detail?.name)) return false;
  }

  if (seats.length) {
    const seatStrings = seats.map(String);
    if (!seatStrings.includes(String(vehicle?.seats))) return false;
  }

  if (minPrice != null && minPrice !== "" && Number(vehicle?.day) < Number(minPrice)) return false;
  if (maxPrice != null && maxPrice !== "" && Number(vehicle?.day) > Number(maxPrice)) return false;

  return true;
};

/*******************
@purpose : count of vehicles in the pool that satisfy every filter passed
@Author : INIC
******************/
export const countMatchingVehicles = (pool, filters = {}, colorList = []) =>
  (pool || []).reduce(
    (count, vehicle) => (vehicleMatchesFilters(vehicle, filters, colorList) ? count + 1 : count),
    0
  );
